import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  INSURANCE_DOMAIN,
  MAIN_DOMAIN,
  SITE_MAP,
} from 'globalConstants';
import { isEqual } from 'lodash';
import type { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { Fragment } from 'react';
import { detectDeviceByUserAgent } from 'utils';

const MobileDiscovery = dynamic(() => import('components/mobile/discovery'));
const Discovery = dynamic(() => import('components/desktop/discovery'));

export const categories = [
  {
    name: 'Dịch vụ gia tăng',
    services: [
      {
        icon: '/icons/discovery/protection.svg',
        label: 'Bảo hiểm',
        href: INSURANCE_DOMAIN,
      },
      {
        icon: '/icons/discovery/laws.svg',
        label: 'Văn phòng công chứng',
        href: `/${SITE_MAP.NOTARIZATION.INDEX}`,
      },
      {
        icon: '/icons/discovery/map.svg',
        label: 'Văn phòng đăng ký',
        href: `/${SITE_MAP.OFFICE_REGISTRATION.INDEX}`,
      },
      {
        icon: '/icons/discovery/bank.svg',
        label: 'Ngân hàng',
        href: `/${SITE_MAP.BANK_LOAN.INDEX}`,
      },
      {
        icon: '/icons/discovery/shopping2.svg',
        label: 'Mua sắm',
        href: `/${SITE_MAP.E_COMMERCE.INDEX}`,
      },
    ],
  },
  {
    name: 'Điện thoại',
    services: [
      {
        icon: '/icons/discovery/sim-card.svg',
        label: 'Sim số đẹp',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/sim-so-dep`,
      },
      {
        icon: '/icons/discovery/device-phone-mobile.svg',
        label: 'Nạp tiền điện thoại',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/nap-tien-dien-thoai`,
      },
      {
        icon: '/icons/discovery/sim-card2.svg',
        label: 'Data 3G/4G',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/data-3-g-4-g`,
      },
      {
        icon: '/icons/discovery/device-phone-mobile2.svg',
        label: 'Mua thẻ điện thoại',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/mua-the-dien-thoai`,
      },
    ],
  },
  {
    name: 'Thanh toán hoá đơn',
    services: [
      {
        icon: '/icons/discovery/receipt.svg',
        label: 'Hóa đơn',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/hoa-don`,
      },
      {
        icon: '/icons/discovery/game-controller.svg',
        label: 'Thanh toán dịch vụ',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/thanh-toan-dich-vu`,
      },
    ],
  },
  {
    name: 'Du lịch - đi lại',
    services: [
      {
        icon: '/icons/discovery/plane-line.svg',
        label: 'Vé máy bay',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/ve-may-bay`,
      },
      {
        icon: '/icons/discovery/plane-ticket.svg',
        label: 'Booking tour',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/booking-tour`,
      },
      {
        icon: '/icons/discovery/hotel.svg',
        label: 'Booking khách sạn',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/booking-khach-san`,
      },
      {
        icon: '/icons/discovery/vietjet.svg',
        label: 'VietJet',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/vietjet`,
      },
    ],
  },
  {
    name: 'Giáo dục',
    services: [
      {
        icon: '/icons/discovery/elsa.svg',
        label: 'Elsa Pro',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/elsa-pro`,
      },
      {
        icon: '/icons/discovery/course.svg',
        label: 'Mua khoá học',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/mua-khoa-hoc`,
      },
    ],
  },
  {
    name: 'Dịch vụ y tế',
    services: [
      {
        icon: '/icons/discovery/health.svg',
        label: 'Đặt lịch khám',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/dat-lich-kham`,
      },
    ],
  },
  {
    name: 'Giải trí - cộng đồng',
    services: [
      {
        icon: '/icons/discovery/cinema.svg',
        label: 'Đặt vé phim',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/dat-ve-phim`,
      },
      {
        icon: '/icons/discovery/vieon.svg',
        label: 'VieON',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/vie-on`,
      },
      {
        icon: '/icons/discovery/tinder.svg',
        label: 'Tinder',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/tinder`,
      },
    ],
  },
  {
    name: 'Giao thông - vận chuyển',
    services: [
      {
        icon: '/icons/discovery/towing-vehicle.svg',
        label: 'Cứu hộ xe',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/cuu-ho-xe`,
      },
      {
        icon: '/icons/discovery/vetc.svg',
        label: 'VETC',
        href: `/${SITE_MAP.DISCOVERY.INDEX}/vetc`,
      },
    ],
  },
];
export interface Category {
  name: string;
  services: Service[];
}
export interface Service {
  icon: string;
  label: string;
  href: string;
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req } = context;
  const { headers } = req;
  const device = detectDeviceByUserAgent(headers['user-agent']);

  return {
    props: {
      device,
    },
  };
};

interface Props {
  device?: 'mobile' | 'desktop';
}

const DiscoveryPage: NextPage = (props: Props) => {
  const title = `Khám phá | ${DEFAULT_TITLE}`;
  const description = DEFAULT_DESCRIPTION;
  const ogUrl = `${MAIN_DOMAIN}/${SITE_MAP.DISCOVERY.INDEX}`;
  const ogImage = DEFAULT_OG_IMAGE;
  const { device } = props;

  return (
    <Fragment>
      <Head>
        <meta
          name='viewport'
          content='width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
        />
        <meta name='description' content={description} />
        <meta name='og:title' content={title} />
        <meta name='og:description' content={description} />
        <meta name='og:url' content={ogUrl} />
        <meta name='og:image' content={ogImage} />
        <title>{title}</title>
      </Head>
      {isEqual(device, 'mobile') ? (
        <MobileDiscovery />
      ) : isEqual(device, 'desktop') ? (
        <Discovery />
      ) : null}
    </Fragment>
  );
};

export default DiscoveryPage;
